import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class Lead extends Base implements EntityType {
    leadId: uuid;
    name: string;
    clients: uuid[];
    closeDate?: Date | null;
    campaigns: uuid[];
    caseTypes: uuid[];
    payers: uuid[];
    estimatedValue?: number | null;
    initialConsultDate?: Date | null;
    referrers: uuid[];
    retainerRequested?: number | null;
    retainerReceived?: number | null;
    paidConsult?: string | null;
    constructor(doc: typeof Lead | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            leadId: genUUID(),
            name: null,
            clients: [],
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "lead";
        // @ts-ignore
        this.leadId = doc._leadId || doc.leadId || doc.leadid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.clients = doc._clients || doc.clients || [];
        // @ts-ignore
        this.closeDate = DateUtil.timestampOnly(doc._closeDate) || DateUtil.timestampOnly(doc.closeDate) || DateUtil.timestampOnly(doc.closedate) || null;
        // @ts-ignore
        this.campaigns = doc._campaigns || doc.campaigns || [];
        // @ts-ignore
        this.caseTypes = doc._caseTypes || doc.caseTypes || doc.casetypes || [];
        // @ts-ignore
        this.payers = doc._payers || doc.payers || [];
        // @ts-ignore
        this.estimatedValue = util.coalesce(util.parseFloat(doc._estimatedValue), util.parseFloat(doc.estimatedValue), util.parseFloat(doc.estimatedvalue), null);
        // @ts-ignore
        this.initialConsultDate = DateUtil.timestampOnly(doc._initialConsultDate) || DateUtil.timestampOnly(doc.initialConsultDate) || DateUtil.timestampOnly(doc.initialconsultdate) || null;
        // @ts-ignore
        this.referrers = doc._referrers || doc.referrers || [];
        // @ts-ignore
        this.retainerRequested = util.coalesce(util.parseFloat(doc._retainerRequested), util.parseFloat(doc.retainerRequested), util.parseFloat(doc.retainerrequested), null);
        // @ts-ignore
        this.retainerReceived = util.coalesce(util.parseFloat(doc._retainerReceived), util.parseFloat(doc.retainerReceived), util.parseFloat(doc.retainerreceived), null);
        // @ts-ignore
        this.paidConsult = util.trimRight(doc._paidConsult) || util.trimRight(doc.paidConsult) || util.trimRight(doc.paidconsult) || null;
    }
    get id(): uuid { return this.leadId; }
    addClient(client: uuid): uuid {
        if (!client) throw new ValidationError('No nulls allowed in an array!');
        if (!this.clients) this.clients = [];
        this.clients.push(client);
        return client;
    }
    updateClient(client: uuid) {
        if (!this.clients) this.clients = [];
        const index = this.clients.findIndex(item => item === client);
        if (index !== -1) { this.clients[index] = client; }
        else this.addClient(client);
    }
    removeClient(client: uuid) {
        if (!this.clients) return;
        let foundItems = this.clients?.filter(item => item === client);
        if (foundItems && foundItems.length > 0) {
            this.clients = this.clients.filter(item => !(item === client));
        }
    }
    abandonClients() { this.clients = []; }
    addCampaign(campaign: uuid): uuid {
        if (!campaign) throw new ValidationError('No nulls allowed in an array!');
        if (!this.campaigns) this.campaigns = [];
        this.campaigns.push(campaign);
        return campaign;
    }
    updateCampaign(campaign: uuid) {
        if (!this.campaigns) this.campaigns = [];
        const index = this.campaigns.findIndex(item => item === campaign);
        if (index !== -1) { this.campaigns[index] = campaign; }
        else this.addCampaign(campaign);
    }
    removeCampaign(campaign: uuid) {
        if (!this.campaigns) return;
        let foundItems = this.campaigns?.filter(item => item === campaign);
        if (foundItems && foundItems.length > 0) {
            this.campaigns = this.campaigns.filter(item => !(item === campaign));
        }
    }
    abandonCampaigns() { this.campaigns = []; }
    addCaseType(caseType: uuid): uuid {
        if (!caseType) throw new ValidationError('No nulls allowed in an array!');
        if (!this.caseTypes) this.caseTypes = [];
        this.caseTypes.push(caseType);
        return caseType;
    }
    updateCaseType(caseType: uuid) {
        if (!this.caseTypes) this.caseTypes = [];
        const index = this.caseTypes.findIndex(item => item === caseType);
        if (index !== -1) { this.caseTypes[index] = caseType; }
        else this.addCaseType(caseType);
    }
    removeCaseType(caseType: uuid) {
        if (!this.caseTypes) return;
        let foundItems = this.caseTypes?.filter(item => item === caseType);
        if (foundItems && foundItems.length > 0) {
            this.caseTypes = this.caseTypes.filter(item => !(item === caseType));
        }
    }
    abandonCaseTypes() { this.caseTypes = []; }
    addPayer(payer: uuid): uuid {
        if (!payer) throw new ValidationError('No nulls allowed in an array!');
        if (!this.payers) this.payers = [];
        this.payers.push(payer);
        return payer;
    }
    updatePayer(payer: uuid) {
        if (!this.payers) this.payers = [];
        const index = this.payers.findIndex(item => item === payer);
        if (index !== -1) { this.payers[index] = payer; }
        else this.addPayer(payer);
    }
    removePayer(payer: uuid) {
        if (!this.payers) return;
        let foundItems = this.payers?.filter(item => item === payer);
        if (foundItems && foundItems.length > 0) {
            this.payers = this.payers.filter(item => !(item === payer));
        }
    }
    abandonPayers() { this.payers = []; }
    addReferrer(referrer: uuid): uuid {
        if (!referrer) throw new ValidationError('No nulls allowed in an array!');
        if (!this.referrers) this.referrers = [];
        this.referrers.push(referrer);
        return referrer;
    }
    updateReferrer(referrer: uuid) {
        if (!this.referrers) this.referrers = [];
        const index = this.referrers.findIndex(item => item === referrer);
        if (index !== -1) { this.referrers[index] = referrer; }
        else this.addReferrer(referrer);
    }
    removeReferrer(referrer: uuid) {
        if (!this.referrers) return;
        let foundItems = this.referrers?.filter(item => item === referrer);
        if (foundItems && foundItems.length > 0) {
            this.referrers = this.referrers.filter(item => !(item === referrer));
        }
    }
    abandonReferrers() { this.referrers = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this lead"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.leadId; }
    static override getNonRefArrayFields(): string[] { return ["clients", "campaigns", "caseTypes", "payers", "referrers"]; }
    static override getDefaultEntries(): Lead[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'lead',
    dataArea: 'Leads and Marketing',
    attachedTo: {parents: ['contact'], via: 'clients', onDelete: 'block', onReplace: 'move'},
    table: 'LEAD',
    comment: 'A prospective-client intake record that is created when a referral is made or a marketing response (call, email, etc.) is received. It tracks the potential client(s), associated case types, initial consult scheduling, retainer amounts requested and received, and estimated case value. A lead can be converted into one or more cases (matters) when the firm takes on the client; it may also be won, lost, refused, or left waiting.',
    fields: {
        leadId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this lead'
        },

        // Clients and other contacts related to this case
        // Note that we may not know the name of the referral until they call!
        name: {
            type: 'string', required: true, comment: `The lead's name`
        },

        clients: {
            type: 'uuid[]',
            entityType: 'contact',
            required: true,
            comment: 'The clients, empty array means we do not know any names yet'
        },

        closeDate: {
            type: 'timestamp',
            comment: 'Estimated close date (sign and fund retention)'
        },

        campaigns: {
            type: 'uuid[]',
            entityType: 'campaign',
            comment: `The marketing campaigns that brought in this lead's client`
        },

        caseTypes: {
            type: 'uuid[]',
            entityType: 'caseType',
            comment: 'What kind of case type(s) are needed?'
        },

        payers: {
            type: 'uuid[]',
            entityType: 'contact',
            comment: 'Who will be paying the bills?'
        },

        estimatedValue: {
            type: 'money',
            comment: 'What is the estimated value of this case??'
        },

        initialConsultDate: {
            type: 'timestamp',
            comment: 'When is the initial consult set up for?'
        },

        referrers: {
            type: 'uuid[]',
            entityType: 'contact',
            required: false,
            comment: `Who referred this lead's client to the firm`
        },

        retainerRequested: {
            type: 'money',
            comment: 'How much money did we ask for as a retainer?'
        },
        retainerReceived: {
            type: 'money',
            comment: 'How much was the client able to provide for the retainer?'
        },

        /**
         * We need a boolean with a null possible, so the values here are t, f and null.
         */
        paidConsult: {
            type: 'char', comment: 'Whether the initial consult for this lead is charged (defaulted from the case type or system setting; editable per lead)'
        },

    },
    maps: [
        {name: 'caseTypeId', keysFn: l => l.caseTypes, presentFn: (t, k) => t.caseTypes.includes(k)},
        {name: 'campaignId', keysFn: l => l.campaigns, presentFn: (t, k) => t.campaigns.includes(k)},
        {name: 'clients', keysFn: t => t.clients, presentFn: (t, k) => t.clients?.includes(k)},
        {name: 'payers', keysFn: t => t.payers, presentFn: (t, k) => t.payers?.includes(k)},
        {name: 'referrers', keysFn: t => t.referrers, presentFn: (t, k) => t.referrers?.includes(k)}
    ]
});
    }
}
export default Lead;
